import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Search, BaggageClaim, Menu, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { useSiteContent } from "../lib/siteContent";

const MENU_COLS = [
    {
        title: "MEN",
        image: "/assets/MEGAMENU_2.jpg",
        links: [
            ["All Men", "/shop?department=Men"],
            ["T-Shirts", "/shop?department=Men&category=T-Shirts"],
            ["Hoodies", "/shop?department=Men&category=Hoodies"],
            ["Jackets", "/shop?department=Men&category=Jackets"],
            ["Bottoms", "/shop?department=Men&category=Bottoms"],
            ["Caps", "/shop?department=Men&category=Accessories"],
        ],
    },
    {
        title: "WOMEN",
        image: "/assets/CATEGORY_WOMEN.jpg",
        links: [
            ["All Women", "/shop?department=Women"],
            ["Hoodies", "/shop?department=Women&category=Hoodies"],
            ["T-Shirts", "/shop?department=Women&category=T-Shirts"],
            ["Bottoms", "/shop?department=Women&category=Bottoms"],
        ],
    },
    {
        title: "KIDS",
        image: "/assets/CATEGORY_KIDS.jpg",
        links: [
            ["All Kids", "/shop?department=Kids"],
            ["Mini Hoodies", "/shop?department=Kids&category=Hoodies"],
            ["Mini Tees", "/shop?department=Kids&category=T-Shirts"],
            ["Caps", "/shop?department=Kids&category=Accessories"],
        ],
    },
];

const DEFAULT_COLLECTION_LINKS = [
    ["Drop 001", "/shop?collection=Drop 001"],
    ["Snake Edition", "/shop?collection=Snake Edition"],
    ["Raised Reality", "/shop?collection=Raised Reality"],
    ["Essentials", "/shop?collection=Essentials"],
];

export default function Header() {
    const { count, setCartOpen, setSearchOpen } = useStore();
    const { collections } = useSiteContent();
    const COLLECTION_LINKS = collections.length
        ? collections.map((c) => [c.name, `/shop?collection=${encodeURIComponent(c.name)}`])
        : DEFAULT_COLLECTION_LINKS;
    const [hidden, setHidden] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [megaOpen, setMegaOpen] = useState(false);
    const [collectionsOpen, setCollectionsOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [menuImage, setMenuImage] = useState(MENU_COLS[0].image);
    const { scrollY } = useScroll();
    const navigate = useNavigate();

    useMotionValueEvent(scrollY, "change", (v) => {
        const prev = scrollY.getPrevious() || 0;
        setHidden(v > prev && v > 160 && !megaOpen);
        setScrolled(v > 40);
    });

    return (
        <>
            <motion.header
                data-testid="site-header"
                animate={{ y: hidden ? "-110%" : "0%" }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className={`fixed inset-x-0 top-0 z-[100] transition-colors duration-500 ${
                    scrolled || megaOpen ? "bg-[#F2F0EA]/90 backdrop-blur-md" : "bg-transparent"
                }`}
                onMouseLeave={() => setMegaOpen(false)}
            >
                <div className="hidden border-b border-[rgba(10,10,10,0.12)] py-1.5 text-center sm:block" data-testid="announcement-bar">
                    <p className="text-[9px] uppercase tracking-[0.3em] text-[#8E8D8A]">
                        Drop 001 — Now Available <span className="mx-2 text-[#333]">/</span> Complimentary shipping over $300 CAD
                    </p>
                </div>
                <div className="flex items-center justify-between px-3 py-3 sm:px-8 sm:py-4 lg:px-16">
                    <nav className="hidden flex-1 items-center gap-7 md:flex">
                        <button
                            data-testid="nav-shop-link"
                            onMouseEnter={() => setMegaOpen(true)}
                            onClick={() => navigate("/shop")}
                            className={`font-display text-[11px] font-semibold uppercase tracking-[0.25em] transition-colors ${
                                megaOpen ? "text-[#0A0A0A]" : "text-[#8E8D8A] hover:text-[#0A0A0A]"
                            }`}
                        >
                            Shop
                        </button>
                        <div
                            className="relative"
                            onMouseEnter={() => setCollectionsOpen(true)}
                            onMouseLeave={() => setCollectionsOpen(false)}
                        >
                            <button
                                data-testid="nav-collections-link"
                                onClick={() => navigate("/shop")}
                                className={`link-underline font-display text-[11px] font-semibold uppercase tracking-[0.25em] transition-colors ${
                                    collectionsOpen ? "text-[#0A0A0A]" : "text-[#8E8D8A] hover:text-[#0A0A0A]"
                                }`}
                            >
                                Collections
                            </button>
                            <AnimatePresence>
                                {collectionsOpen && (
                                    <motion.div
                                        data-testid="collections-dropdown"
                                        initial={{ opacity: 0, y: 10, clipPath: "inset(0 0 100% 0)" }}
                                        animate={{ opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" }}
                                        exit={{ opacity: 0, y: 10, clipPath: "inset(0 0 100% 0)" }}
                                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                        className="absolute left-0 top-full mt-4 w-60 border border-[rgba(10,10,10,0.12)] bg-[#F2F0EA] py-4 shadow-[0_30px_60px_-20px_rgba(10,10,10,0.25)]"
                                    >
                                        {COLLECTION_LINKS.map(([label, to], i) => (
                                            <motion.div
                                                key={label}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.08 + i * 0.05, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                            >
                                                <Link
                                                    data-testid={`collections-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                                    to={to}
                                                    onClick={() => setCollectionsOpen(false)}
                                                    className="block px-6 py-2.5 font-display text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6B6A66] transition-colors hover:text-[#0A0A0A]"
                                                >
                                                    {label}
                                                </Link>
                                            </motion.div>
                                        ))}
                                        <div className="mx-6 my-2 border-t border-[rgba(10,10,10,0.12)]" />
                                        <motion.div
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.32, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                        >
                                            <Link
                                                data-testid="collections-link-show-all"
                                                to="/shop"
                                                onClick={() => setCollectionsOpen(false)}
                                                className="block px-6 py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.2em] text-[#0A0A0A]"
                                            >
                                                Show All
                                            </Link>
                                        </motion.div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </nav>
                    <div className="flex flex-1 items-center md:hidden">
                        <div className="flex items-center gap-1 rounded-full border border-[rgba(10,10,10,0.15)] bg-[rgba(242,240,234,0.65)] py-2 pl-3 pr-4 backdrop-blur-md">
                            <button data-testid="mobile-menu-button" onClick={() => setMobileOpen(true)} aria-label="Open menu">
                                <Menu size={15} strokeWidth={1.8} />
                            </button>
                            <Link to="/" className="font-display text-[10px] font-extrabold uppercase tracking-[0.25em] text-[#0A0A0A]">
                                Vantage
                            </Link>
                        </div>
                    </div>
                    <Link
                        data-testid="nav-logo"
                        to="/"
                        className="hidden font-display text-center text-sm font-extrabold uppercase leading-tight tracking-[0.3em] text-[#0A0A0A] md:block"
                    >
                        Vantage
                        <span className="block text-[8px] font-medium tracking-[0.5em] text-[#8E8D8A]">Owners Club</span>
                    </Link>
                    <div className="hidden flex-1 items-center justify-end gap-7 md:flex">
                        <button
                            data-testid="search-open-button"
                            onClick={() => setSearchOpen(true)}
                            aria-label="Search"
                            className="text-[#8E8D8A] transition-colors hover:text-[#0A0A0A]"
                        >
                            <Search size={17} strokeWidth={1.5} />
                        </button>
                        <button
                            data-testid="cart-open-button"
                            onClick={() => setCartOpen(true)}
                            className="font-display text-[11px] font-semibold uppercase tracking-[0.25em] text-[#0A0A0A]"
                        >
                            Bag
                            <motion.span key={count} initial={{ scale: 1.5 }} animate={{ scale: 1 }} className="ml-1 inline-block">
                                ({count})
                            </motion.span>
                        </button>
                    </div>
                    <div className="flex flex-1 items-center justify-end gap-2 md:hidden">
                        <button
                            data-testid="search-open-button-mobile"
                            onClick={() => setSearchOpen(true)}
                            aria-label="Search"
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(10,10,10,0.15)] bg-[rgba(242,240,234,0.65)] backdrop-blur-md"
                        >
                            <Search size={15} strokeWidth={1.8} />
                        </button>
                        <button
                            data-testid="cart-open-button-mobile"
                            onClick={() => setCartOpen(true)}
                            aria-label="Open bag"
                            className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(10,10,10,0.15)] bg-[rgba(242,240,234,0.65)] backdrop-blur-md"
                        >
                            <BaggageClaim size={15} strokeWidth={1.8} />
                            {count > 0 && (
                                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#0A0A0A] px-1 text-[8px] font-bold text-[#F2F0EA]">
                                    {count}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
                <AnimatePresence>
                    {megaOpen && (
                        <motion.div
                            data-testid="mega-menu"
                            initial={{ clipPath: "inset(0 0 100% 0)", opacity: 0 }}
                            animate={{ clipPath: "inset(0 0 0% 0)", opacity: 1 }}
                            exit={{ clipPath: "inset(0 0 100% 0)", opacity: 0 }}
                            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute inset-x-0 top-full hidden border-t border-[rgba(10,10,10,0.12)] bg-[#F2F0EA] md:block"
                        >
                            <div className="mx-auto grid max-w-[1500px] grid-cols-4 gap-10 px-16 py-12">
                                {MENU_COLS.map((col, ci) => (
                                    <div key={col.title} onMouseEnter={() => setMenuImage(col.image)}>
                                        <p className="mb-5 text-[9px] uppercase tracking-[0.35em] text-[#6B6A66]">{col.title}</p>
                                        <ul className="space-y-3">
                                            {col.links.map(([label, to], i) => (
                                                <motion.li
                                                    key={label}
                                                    initial={{ opacity: 0, y: 12 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.1 + (ci * 3 + i) * 0.03, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                                >
                                                    <Link
                                                        data-testid={`mega-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                                        to={to}
                                                        onClick={() => setMegaOpen(false)}
                                                        className="link-underline font-display text-sm font-semibold uppercase tracking-[0.15em] text-[#0A0A0A]"
                                                    >
                                                        {label}
                                                    </Link>
                                                </motion.li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                                <div className="relative col-span-1 overflow-hidden">
                                    <AnimatePresence mode="wait">
                                        <motion.img
                                            key={menuImage}
                                            src={menuImage}
                                            alt="Campaign"
                                            initial={{ opacity: 0, scale: 1.04 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                            className="h-64 w-full object-cover"
                                        />
                                    </AnimatePresence>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.header>

            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ clipPath: "inset(0 0 100% 0)" }}
                        animate={{ clipPath: "inset(0 0 0% 0)" }}
                        exit={{ clipPath: "inset(0 0 100% 0)" }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed inset-0 z-[150] flex flex-col bg-[#F2F0EA] px-6 py-6"
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-display text-xs font-bold uppercase tracking-[0.3em]">Vantage</span>
                            <button data-testid="mobile-menu-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">
                                <X size={22} strokeWidth={1.5} />
                            </button>
                        </div>
                        <nav className="mt-14 flex flex-col gap-6 overflow-y-auto">
                            {[
                                ["Shop All", "/shop"],
                                ["Women", "/shop?department=Women"],
                                ["Kids", "/shop?department=Kids"],
                            ].map(([label, to], i) => (
                                <motion.div
                                    key={label}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.15 + i * 0.07, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                    className="overflow-hidden"
                                >
                                    <Link
                                        data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                        to={to}
                                        onClick={() => setMobileOpen(false)}
                                        className="font-display text-3xl font-extrabold uppercase tracking-tight text-[#0A0A0A]"
                                    >
                                        {label}
                                    </Link>
                                </motion.div>
                            ))}
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.36, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                className="mt-4 border-t border-[rgba(10,10,10,0.12)] pt-6"
                            >
                                <p className="mb-4 text-[9px] uppercase tracking-[0.4em] text-[#6B6A66]">Collections</p>
                                <div className="flex flex-col gap-3">
                                    {COLLECTION_LINKS.map(([label, to], i) => (
                                        <motion.div
                                            key={label}
                                            initial={{ opacity: 0, x: -14 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.42 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                        >
                                            <Link
                                                data-testid={`mobile-nav-collection-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                                to={to}
                                                onClick={() => setMobileOpen(false)}
                                                className="font-display text-lg font-semibold uppercase tracking-[0.08em] text-[#6B6A66]"
                                            >
                                                {label}
                                            </Link>
                                        </motion.div>
                                    ))}
                                    <motion.div
                                        initial={{ opacity: 0, x: -14 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.68, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                    >
                                        <Link
                                            data-testid="mobile-nav-collections-show-all"
                                            to="/shop"
                                            onClick={() => setMobileOpen(false)}
                                            className="font-display text-lg font-bold uppercase tracking-[0.08em] text-[#0A0A0A]"
                                        >
                                            Show All
                                        </Link>
                                    </motion.div>
                                </div>
                            </motion.div>
                        </nav>
                        <p className="mt-auto text-[9px] uppercase tracking-[0.35em] text-[#6B6A66]">© 2026 Vantage Owners Club — Canada</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
